// Smart URL detection for the signup flow.
//
// User pastes any homepage URL (their actual website, a franchise location,
// a Facebook/Instagram/TikTok profile, a Google Business Profile listing,
// etc.) and we detect what they typed so the rest of the signup flow can
// morph accordingly (Phases 3+ — currently telemetry + UI chip only).
//
// Detection is LITERAL — based on the string the user typed, not what the
// URL resolves to. We intentionally do NOT follow HTTP redirects: if
// joestiles.com happens to redirect to facebook.com/joestiles, we honor
// what they typed (a website) and let the website flow proceed.
//
// Pure functions; no DB / network. Safe to use in both browser and Node.

const PATTERN_TABLE = [
  // Each entry's hostMatches checks BOTH exact-hostname (modulo www./m./vm.)
  // and ".something" subdomain endings (e.g. ".myshopify.com").
  {
    pattern: 'facebook',
    label:   'Facebook page',
    hostMatches: ['facebook.com', 'fb.com', 'fb.me', 'fb.watch'],
  },
  {
    pattern: 'instagram',
    label:   'Instagram profile',
    hostMatches: ['instagram.com', 'instagr.am'],
  },
  {
    pattern: 'linkedin',
    label:   'LinkedIn page',
    hostMatches: ['linkedin.com', 'lnkd.in'],
  },
  {
    pattern: 'twitter',
    label:   'X (Twitter) profile',
    hostMatches: ['twitter.com', 'x.com', 't.co'],
  },
  {
    pattern: 'tiktok',
    label:   'TikTok profile',
    hostMatches: ['tiktok.com'],
  },
  {
    pattern: 'youtube',
    label:   'YouTube channel',
    hostMatches: ['youtube.com', 'youtu.be'],
  },
  {
    pattern: 'gbp',
    label:   'Google Business Profile',
    hostMatches: ['g.page', 'business.google.com', 'maps.google.com', 'posts.gle'],
  },
  {
    pattern: 'yelp',
    label:   'Yelp listing',
    hostMatches: ['yelp.com', 'yelp.to'],
  },
  {
    pattern: 'nextdoor',
    label:   'Nextdoor business',
    hostMatches: ['nextdoor.com'],
  },
  {
    pattern: 'etsy',
    label:   'Etsy shop',
    hostMatches: ['etsy.com'],
  },
  {
    pattern: 'shopify',
    label:   'Shopify store',
    hostMatches: ['.myshopify.com'],
  },
  {
    pattern: 'square',
    label:   'Square Online store',
    hostMatches: ['.square.site', 'squareup.com'],
  },
  {
    pattern: 'wix',
    label:   'Wix site',
    hostMatches: ['.wixsite.com', 'wix.com'],
  },
  {
    pattern: 'wordpress',
    label:   'WordPress.com site',
    hostMatches: ['.wordpress.com'],
  },
  {
    pattern: 'weebly',
    label:   'Weebly site',
    hostMatches: ['.weebly.com'],
  },
];

function parseUrl(raw) {
  const cleaned = String(raw || '').trim();
  if (!cleaned) return null;
  // Prepend protocol if missing — URL parser rejects bare domains otherwise.
  const urlStr = /^https?:\/\//i.test(cleaned) ? cleaned : 'https://' + cleaned;
  let url;
  try {
    url = new URL(urlStr);
  } catch {
    return null;
  }
  // URL parser is lenient — it accepts bare strings like "something" as a
  // valid hostname. Reject anything without a TLD-shaped suffix (.com,
  // .agency, .co.uk etc). Allows multi-letter TLDs (matches real customers
  // like heritagerealestate.agency) and rejects bare words + raw IPs.
  if (!/\.[a-z]{2,}$/i.test(url.hostname)) return null;
  return url;
}

// URL shapes that are GUARANTEED to be personal profiles (not Business
// Pages / Company Pages / Business Accounts). Phase 3+ rejects these
// before OAuth since the API path won't work anyway.
//
// NOTE on Facebook: profile.php URLs are NOT a reliable personal-profile
// signal. Facebook's New Pages Experience (2021+) gives Business Pages
// numeric-ID URLs that LOOK like profile.php?id=... — same URL shape as
// personal profiles. We can't distinguish from the URL alone. Defer the
// decision to OAuth: if the typed numeric ID matches a Page in the
// user's /me/accounts response, it's a Page; otherwise reject post-OAuth.
function isPersonalProfileShape(pattern, pathPart) {
  if (pattern === 'linkedin') {
    // linkedin.com/in/foo is personal; linkedin.com/company/foo is the
    // Company Page we want. Unambiguous from URL alone.
    return /^\/in($|\/)/i.test(pathPart);
  }
  return false;
}

function hostMatchesEntry(host, entry) {
  for (const m of entry.hostMatches) {
    if (m.startsWith('.')) {
      // Subdomain match: ".myshopify.com" matches "joestiles.myshopify.com".
      if (host.endsWith(m) && host.length > m.length) return true;
    } else {
      // Exact match plus common mobile/short-form prefixes.
      if (host === m) return true;
      if (host === 'www.' + m) return true;
      if (host === 'm.' + m) return true;
      if (host === 'vm.' + m) return true;
    }
  }
  return false;
}

/**
 * Detect what kind of URL the user typed.
 *
 * @param {string} raw - The raw string from the input field.
 * @returns {{
 *   pattern: string,          // 'website' | 'franchise' | 'facebook' | ... | 'unknown'
 *   label: string,            // human-friendly chip text
 *   hostname: string|null,    // detected hostname (after www. strip)
 *   normalized: string|null,  // canonical "host" or "host/path" form
 *   bareDomain: string|null,  // hostname only (no path, no www.)
 * }}
 */
function detectSignupUrl(raw) {
  const url = parseUrl(raw);
  if (!url) {
    return {
      pattern: 'unknown',
      label: 'Unrecognized URL',
      hostname: null,
      normalized: null,
      bareDomain: null,
    };
  }

  const hostRaw = url.hostname.toLowerCase();
  const host = hostRaw.replace(/^www\./, '');
  const pathPart = (url.pathname || '').replace(/\/+$/, '');
  const hasPath = pathPart && pathPart !== '';

  // Special case: desktop Google Maps' address bar shows URLs as
  // www.google.com/maps/place/<biz>/... — not maps.google.com. We can't
  // just add google.com to the gbp hostMatches list (would over-match
  // every google.com URL), so handle this shape explicitly: host
  // google.com + path /maps/* = Google Business Profile. Same treatment
  // as the existing maps.google.com hostMatch — the URL points at a
  // Maps listing, our gbpVerification extractor handles both.
  if (host === 'google.com' && /^\/maps(\/|$)/i.test(pathPart)) {
    return {
      pattern: 'gbp',
      label:   'Google Business Profile',
      hostname: host,
      normalized: host + pathPart,
      bareDomain: host,
    };
  }

  for (const entry of PATTERN_TABLE) {
    if (hostMatchesEntry(hostRaw, entry)) {
      // Personal-profile sniff: certain URL shapes are guaranteed to be
      // personal profiles (not Business Pages), which we don't support.
      // Flag them so the UI can show a clear rejection BEFORE OAuth.
      const isPersonal = isPersonalProfileShape(entry.pattern, pathPart);

      // FB's New Pages Experience gives Business Pages URLs of the form
      // facebook.com/profile.php?id=<numeric>. We pull the ID out so the
      // OAuth callback can cross-check it against the user's /me/accounts
      // (Page list). Without the ID, normalized would just be /profile.php
      // and the cross-check would have nothing to match on.
      let platformId = null;
      if (entry.pattern === 'facebook' && /^\/profile\.php$/i.test(pathPart)) {
        platformId = url.searchParams.get('id');
      }
      const normalized = platformId
        ? `${host}${pathPart}?id=${platformId}`
        : host + pathPart;

      return {
        pattern: entry.pattern,
        label:   entry.label,
        hostname: host,
        normalized,
        bareDomain: host,
        isPersonal,
        platformId,
      };
    }
  }

  // Not a known platform — generic website OR franchise (has a sub-path).
  if (hasPath) {
    return {
      pattern: 'franchise',
      label:   'Franchise location',
      hostname: host,
      normalized: host + pathPart,
      bareDomain: host,
    };
  }
  return {
    pattern: 'website',
    label:   'Website',
    hostname: host,
    normalized: host,
    bareDomain: host,
  };
}

/**
 * Pull the platform handle out of a detected URL. Used by Phase 3+ OAuth
 * signup flows to cross-check that the user OAuths into the same Page they
 * typed. Pattern-aware: linkedin.com/company/joestiles → 'joestiles' (skips
 * the 'company' segment); facebook.com/joestiles → 'joestiles';
 * tiktok.com/@joestiles → 'joestiles' (strips the @).
 *
 * @returns {string|null}
 */
function extractHandle(detection) {
  if (!detection || !detection.normalized) return null;
  // Numeric-ID URLs (e.g. FB's profile.php?id=12345) — prefer the ID
  // since FB's /me/accounts response has a matching `id` field per Page.
  if (detection.platformId) return detection.platformId;
  const segs = detection.normalized.split('/').slice(1).filter(Boolean);
  if (!segs.length) return null;
  switch (detection.pattern) {
    case 'facebook':
    case 'instagram':
    case 'tiktok':
      return (segs[0] || '').replace(/^@/, '') || null;
    case 'linkedin':
      // linkedin.com/company/foo → 'foo'; linkedin.com/in/foo → 'foo'
      return segs[1] || segs[0] || null;
    case 'twitter':
    case 'youtube':
      return (segs[0] || '').replace(/^@/, '') || null;
    case 'gbp':
      return segs[segs.length - 1] || null;
    default:
      return segs[segs.length - 1] || null;
  }
}

module.exports = {
  detectSignupUrl,
  extractHandle,
  // Exported for tests:
  PATTERN_TABLE,
  parseUrl,
};
